<!-- Loading State -->
@if (!emailInitial) {
<div class="editor-loading">
    <mat-spinner diameter="40"></mat-spinner>
    <span>Loading template...</span>
</div>
} @else {
<!-- Editor Header -->
<div class="editor-header">
    <form [formGroup]="emailForm" #emForm="ngForm" class="header-form">
        <!-- Left Section: Template Info + Published Status -->
        <div class="header-left">
            <div class="template-info">
                <mat-icon class="template-icon">description</mat-icon>
                <span class="template-name">{{ emailForm.value.templateName }}</span>
            </div>

            <!-- Published Status Button - clickable to open compare screen -->
            @if (emailInitial.publishedSnapshot) {
            <button class="btn-published-status" (click)="openDiffDialog()"
                [matTooltip]="hasUnpublishedChanges() ? 'Click to compare with published version' : 'Click to view published version'">
                <mat-icon>check_circle</mat-icon>
                <span>Published {{ publishedTimeAgo() }}</span>
                @if (hasUnpublishedChanges()) {
                <span class="changes-indicator"></span>
                }
            </button>
            } @else {
            <span class="status-never-published">
                <mat-icon>info_outline</mat-icon>
                Never Published
            </span>
            }
        </div>

        <!-- Right Section: Actions (simplified) -->
        <div class="header-actions">
            <!-- Revert Button (only shown when there are changes) -->
            @if (hasChanges()) {
            <button class="btn-revert" (click)="revert()" matTooltip="Revert all unsaved changes">
                <mat-icon>undo</mat-icon>
                <span>Revert</span>
            </button>
            }

            <!-- Save Button -->
            <button class="btn-save" (click)="save()" [disabled]="!hasChanges() || emailForm.invalid || isSaving()">
                <ng-container *ngIf="isSaving()">
                    <mat-spinner diameter="18" class="save-spinner"></mat-spinner>
                    <span>Saving...</span>
                </ng-container>
                <ng-container *ngIf="!isSaving()">
                    <mat-icon>save</mat-icon>
                    <span>Save</span>
                </ng-container>
            </button>

            <!-- Publish Button -->
            <button class="btn-publish" (click)="publish()" [disabled]="!canPublish() || isPublishing()"
                [matTooltip]="canPublish() ? 'Publish to AWS SES' : publishBlockReason()">
                <ng-container *ngIf="isPublishing()">
                    <mat-spinner diameter="18" class="publish-spinner"></mat-spinner>
                    <span>Publishing...</span>
                </ng-container>
                <ng-container *ngIf="!isPublishing()">
                    <mat-icon>publish</mat-icon>
                    <span>Publish</span>
                </ng-container>
            </button>

            <!-- More Options (Kebab Menu) -->
            <button class="btn-more" [matMenuTriggerFor]="options">
                <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #options="matMenu">
                <button mat-menu-item (click)="editDescription()">
                    <mat-icon>edit</mat-icon>
                    <span>Edit Description</span>
                </button>
                <button mat-menu-item (click)="cloneTemplate()">
                    <mat-icon>content_copy</mat-icon>
                    <span>Clone Template</span>
                </button>
                @if (isAdmin) {
                <button mat-menu-item class="delete-option" (click)="deleteHandler()">
                    <mat-icon>delete_outline</mat-icon>
                    <span>Delete Template</span>
                </button>
                }
            </mat-menu>
        </div>
    </form>

    <!-- Subject Line -->
    <div class="subject-row">
        @if (showVariableValues()) {
        <!-- Read-only subject with resolved variables -->
        <mat-form-field appearance="outline" class="subject-field readonly-subject">
            <mat-label>Email Subject</mat-label>
            <mat-icon matPrefix>subject</mat-icon>
            <input matInput [value]="displaySubject()" type="text" readonly />
        </mat-form-field>
        } @else {
        <!-- Editable subject field - use @for with track to force re-render on language change -->
        @for (lang of [currLangCode()]; track lang) {
        <mat-form-field appearance="outline" class="subject-field"
            [class.edited]="emailInitial && (emailForm.value.subjectPart[emailInitial.defaultLangCode] !== emailInitial.subjectPart[emailInitial.defaultLangCode])"
            [class.has-variable-errors]="subjectValidationErrors().length > 0">
            <mat-label>Email Subject</mat-label>
            <mat-icon matPrefix>subject</mat-icon>
            <input matInput [formControl]="$any(subjectGroup.get(lang))" type="text"
                placeholder="Enter email subject..."
                [attr.dir]="isRtlLanguage(lang) ? 'rtl' : null" />

            <!-- Error messages for undefined variables -->
            @if (subjectValidationErrors().length > 0) {
            @for (error of subjectValidationErrors(); track error) {
            <mat-error>{{ error }}</mat-error>
            }
            }

            <!-- Existing required validator error -->
            <mat-error *ngIf="subjectControl.hasError('required') && !subjectValidationErrors().length">
                Subject is required
            </mat-error>
        </mat-form-field>
        }
        }
    </div>
</div>

<!-- Editor Body -->
<div class="editor-container">
    <div class="editor-panels">
        <!-- HTML Editor Panel -->
        <div class="panel html-panel">
            <div class="panel-header">
                <div class="header-title">
                    <mat-icon>code</mat-icon>
                    <span>HTML Editor</span>
                </div>
                <!-- Validation Status Indicators -->
                @if (htmlValidationErrors().length > 0) {
                <div class="validation-status-text">
                    @if (getErrorCount() > 0) {
                    <span class="status-text error">
                        <mat-icon>error</mat-icon>
                        {{ getErrorCount() }} {{ getErrorCount() === 1 ? 'error' : 'errors' }}
                    </span>
                    }
                    @if (getWarningCount() > 0) {
                    <span class="status-text warning">
                        <mat-icon>warning</mat-icon>
                        {{ getWarningCount() }} {{ getWarningCount() === 1 ? 'warning' : 'warnings' }}
                    </span>
                    }
                </div>
                } @else if (templateControl.value && templateControl.value.trim()) {
                <div class="validation-status-text">
                    <span class="status-text valid">
                        <mat-icon>check_circle</mat-icon>
                        Valid HTML
                    </span>
                </div>
                }
                <!-- AI Assistant & Undo Buttons -->
                <div class="header-controls">
                    <!-- Format HTML Button with AI -->
                    <button class="control-btn format-btn" (click)="formatAllHtmlContent()"
                        [disabled]="!templateControl.value || !templateControl.value.trim() || aiFormattingLoading()"
                        [matTooltip]="aiFormattingLoading() ? 'AI is formatting...' : 'Format and optimize HTML with AI'">
                        @if (aiFormattingLoading()) {
                        <mat-spinner diameter="18"></mat-spinner>
                        } @else {
                        <mat-icon>auto_fix_high</mat-icon>
                        }
                    </button>
                    <!-- Auto-fix Validation Errors Button -->
                    <button class="control-btn validation-fix-btn" (click)="autoFixValidationErrors()"
                        [disabled]="aiAssistantLoading()"
                        [matTooltip]="aiAssistantLoading() ? 'Fixing errors...' : 'Auto-fix validation errors with AI'">
                        @if (aiAssistantLoading()) {
                        <mat-spinner diameter="18"></mat-spinner>
                        } @else {
                        <mat-icon>build</mat-icon>
                        }
                    </button>
                    <button class="ai-assistant-btn" [class.active]="aiAssistantOpen()" (click)="toggleAiAssistant()"
                        matTooltip="AI Assistant">
                        <mat-icon>auto_awesome</mat-icon>
                    </button>
                </div>
            </div>
            <!-- AI Assistant Popup -->
            @if (aiAssistantOpen()) {
            <div class="ai-assistant-popup">
                <div class="ai-popup-header">
                    <mat-icon>auto_awesome</mat-icon>
                    <span>AI Assistant</span>
                    <button class="ai-close-btn" (click)="closeAiAssistant()">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>

                <!-- Mode Toggle Tabs -->
                <div class="ai-mode-tabs">
                    <button class="ai-mode-tab" [class.active]="!aiImageMode()"
                        (click)="aiImageMode() && toggleAiImageMode()" [disabled]="aiAssistantLoading()">
                        <mat-icon>edit</mat-icon>
                        <span>Edit</span>
                    </button>
                    <button class="ai-mode-tab" [class.active]="aiImageMode()"
                        (click)="!aiImageMode() && toggleAiImageMode()" [disabled]="aiAssistantLoading()">
                        <mat-icon>image</mat-icon>
                        <span>From Image</span>
                    </button>
                </div>

                <div class="ai-popup-body">
                    @if (!aiImageMode()) {
                    <!-- Text Instruction Mode -->
                    <textarea class="ai-instruction-input"
                        placeholder="Describe what changes you want... e.g., 'Add a footer with social media links' or 'Make the header blue with white text'"
                        [value]="aiInstruction()" (input)="onAiInstructionChange($event)"
                        (keydown)="onAiKeydown($event)" [disabled]="aiAssistantLoading()" rows="3"></textarea>
                    <p class="ai-hint">Press <kbd>⌘</kbd>+<kbd>Enter</kbd> to apply</p>
                    } @else {
                    <!-- Image Upload Mode -->
                    @if (!aiImagePreview()) {
                    <div class="ai-image-upload-area" (drop)="onAiImageDrop($event)"
                        (dragover)="onAiImageDragOver($event)">
                        <input #aiImageInput type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                            (change)="onAiImageSelected($event)" hidden>
                        <mat-icon class="upload-icon">image</mat-icon>
                        <p class="upload-title">Upload a reference image</p>
                        <p class="upload-subtitle">Design mockup, screenshot, or visual reference</p>
                        <button class="browse-btn" (click)="aiImageInput.click(); $event.stopPropagation()">
                            <mat-icon>folder_open</mat-icon>
                            Browse Files
                        </button>
                        <p class="upload-formats">or drag & drop here (PNG, JPEG, WebP, GIF)</p>
                    </div>
                    } @else {
                    <div class="ai-image-preview-container">
                        <img [src]="aiImagePreview()" alt="Reference image" class="ai-image-preview">
                        <button class="ai-image-remove-btn" (click)="clearAiImage()" [disabled]="aiAssistantLoading()">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                    <textarea class="ai-instruction-input"
                        placeholder="Describe what you want... e.g., 'Create an email template matching this design' or 'Fix the spacing issues shown in this screenshot' or 'Use this as inspiration for the header'"
                        [value]="aiInstruction()" (input)="onAiInstructionChange($event)"
                        [disabled]="aiAssistantLoading()" rows="3"></textarea>
                    }
                    }
                </div>
                <div class="ai-popup-footer">
                    @if (!aiImageMode()) {
                    <button class="ai-apply-btn" (click)="submitAiInstruction()"
                        [disabled]="!aiInstruction().trim() || aiAssistantLoading()">
                        @if (aiAssistantLoading()) {
                        <mat-spinner diameter="16"></mat-spinner>
                        <span>Applying...</span>
                        } @else {
                        <mat-icon>auto_awesome</mat-icon>
                        <span>Apply</span>
                        }
                    </button>
                    } @else {
                    <button class="ai-apply-btn" (click)="submitAiImage()"
                        [disabled]="!aiImageFile() || !aiInstruction().trim() || aiAssistantLoading()">
                        @if (aiAssistantLoading()) {
                        <mat-spinner diameter="16"></mat-spinner>
                        <span>Processing...</span>
                        } @else {
                        <mat-icon>auto_awesome</mat-icon>
                        <span>Apply</span>
                        }
                    </button>
                    }
                </div>
            </div>
            }
            <div class="panel-content">
                <!-- Use @for with track to force re-render on language change -->
                @for (lang of [currLangCode()]; track lang) {
                @if (lang === emailForm.value.defaultLangCode || emailForm.value.htmlPart[lang]) {
                <ngx-codemirror class="code-editor" #codemirror [options]="codeMirrorOptions"
                    [formControl]="$any(templateGroup.get(lang))">
                </ngx-codemirror>
                } @else {
                <ng-container *ngTemplateOutlet="translationPending"></ng-container>
                }
                }
                <!-- Validation Errors Panel -->
                @if (htmlValidationErrors().length > 0) {
                <div class="validation-errors-panel" [class.has-errors]="getErrorCount() > 0">
                    <div class="errors-header">
                        <mat-icon>{{ getErrorCount() > 0 ? 'error_outline' : 'warning_amber' }}</mat-icon>
                        <span>HTML Validation Issues</span>
                    </div>
                    <ul class="errors-list">
                        @for (error of htmlValidationErrors(); track error.message) {
                        <li [class.error]="error.type === 'error'" [class.warning]="error.type === 'warning'"
                            [class.clickable]="error.line" (click)="error.line ? jumpToLine(error.line) : null">
                            <mat-icon>{{ error.type === 'error' ? 'cancel' : 'warning' }}</mat-icon>
                            <span class="error-message">
                                {{ error.message }}
                                @if (error.line) {
                                <span class="error-line">(line {{ error.line }})</span>
                                }
                            </span>
                        </li>
                        }
                    </ul>
                </div>
                }
            </div>
        </div>

        <!-- Preview Panel -->
        <div class="panel preview-panel" [class.dark-mode]="previewDarkMode()">
            <div class="panel-header">
                <div class="header-title">
                    <mat-icon>visibility</mat-icon>
                    <span>Preview</span>
                </div>

                <div class="header-controls">
                    <!-- Desktop/Mobile Toggle -->
                    <div class="view-toggles">
                        <button class="toggle-btn icon-only" [class.active]="previewViewMode() === 'desktop'"
                            (click)="setPreviewViewMode('desktop')" matTooltip="Desktop view">
                            <mat-icon>desktop_windows</mat-icon>
                        </button>
                        <button class="toggle-btn icon-only" [class.active]="previewViewMode() === 'mobile'"
                            (click)="setPreviewViewMode('mobile')" matTooltip="Mobile view">
                            <mat-icon>smartphone</mat-icon>
                        </button>
                    </div>

                    <!-- Variable Display Toggle -->
                    @if (emailForm.value.templateVariables?.length > 0) {
                    <div class="divider"></div>
                    <button class="control-btn" [class.active]="showVariableValues()" (click)="toggleVariableDisplay()"
                        [matTooltip]="showVariableValues() ? 'Show Placeholders' : 'Show Variable Values'">
                        <mat-icon>{{ showVariableValues() ? 'code_off' : 'data_object' }}</mat-icon>
                    </button>
                    }

                    <div class="divider"></div>

                    <!-- Dark Mode Toggle -->
                    <button class="theme-toggle" [class.dark]="previewDarkMode()" (click)="togglePreviewDarkMode()"
                        [matTooltip]="previewDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
                        <mat-icon>{{ previewDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
                    </button>

                    <div class="divider"></div>

                    <!-- Test Email Button -->
                    <button class="control-btn" (click)="openTestEmailDialog()" [disabled]="!canSendTestEmail()"
                        [matTooltip]="canSendTestEmail() ? 'Send test email' : testEmailBlockReason()">
                        <mat-icon>send</mat-icon>
                    </button>

                    <div class="divider"></div>

                    <!-- Fullscreen Toggle -->
                    <button class="control-btn" (click)="togglePreviewFullscreen()" matTooltip="Fullscreen preview">
                        <mat-icon>fullscreen</mat-icon>
                    </button>
                </div>
            </div>
            <div class="panel-content preview-body">
                <!-- Use @for with track to force re-render on language change -->
                @for (lang of [currLangCode()]; track lang) {
                @if (lang === emailForm.value.defaultLangCode || emailForm.value.htmlPart[lang]) {
                <div class="preview-container" [class.mobile-view]="previewViewMode() === 'mobile'">
                    <!-- Device Frame -->
                    <div class="device-frame">
                        <!-- Email Client UI Wrapper -->
                        <div class="email-client-ui">
                            <!-- Email Metadata Header -->
                            <div class="email-meta-header">
                                <div class="subject-line">{{ displaySubject() || 'No Subject' }}</div>
                                <div class="sender-info">
                                    <div class="avatar">
                                        <span>M</span>
                                    </div>
                                    <div class="meta-details">
                                        <div class="from-line">
                                            <span class="name">Mevolve</span>
                                            <span class="email">&lt;info&#64;mevolve.app&gt;</span>
                                        </div>
                                        <div class="to-line">to me</div>
                                    </div>
                                    <div class="date-info">
                                        {{ 0 | date:'shortTime' }}
                                    </div>
                                </div>
                            </div>

                            <!-- Email Content -->
                            <div class="email-content-wrapper">
                                <iframe class="email-iframe" [srcdoc]="safePreviewHtml()" title="Email Preview"
                                    sandbox="allow-same-origin">
                                </iframe>
                            </div>
                        </div>
                    </div>
                </div>
                } @else {
                <ng-container *ngTemplateOutlet="translationPending"></ng-container>
                }
                }
            </div>
        </div>
    </div>

    <!-- Translation Pending Template -->
    <ng-template #translationPending>
        <div class="translation-pending">
            <mat-icon>translate</mat-icon>
            <p>Translation pending for this language</p>
            <button class="btn-translate-now" (click)="translateTemplate()">Translate Now</button>
        </div>
    </ng-template>
</div>

<!-- Editor Footer -->
<div class="editor-footer">
    <!-- Variables Section -->
    <div class="variables-section">
        <button class="btn-variables"
            [matMenuTriggerFor]="emailForm.value.templateVariables?.length > 0 ? variableNames : null">
            <mat-icon>data_object</mat-icon>
            <span>{{ emailForm.value.templateVariables?.length || 0 }} Variables</span>
        </button>
        <mat-menu #variableNames="matMenu">
            <button mat-menu-item *ngFor="let variable of emailForm.value.templateVariables"
                (click)="openVariableDialog('edit', variable)" [class.system-variable]="variable.isSystemVariable">
                @if (variable.isSystemVariable) {
                <mat-icon class="system-icon">lock</mat-icon>
                } @else {
                <mat-icon>edit</mat-icon>
                }
                <span>{{ variable.name }}</span>
                @if (variable.isSystemVariable) {
                <span class="system-label">System</span>
                }
            </button>
        </mat-menu>
        <button class="btn-add-var" (click)="openVariableDialog('new')" matTooltip="Add Variable">
            <mat-icon>add</mat-icon>
        </button>
    </div>

    <!-- Language Selector -->
    <div class="language-selector">
        <span class="selector-label">Language:</span>
        <div class="lang-scroll-container">
            <button class="scroll-arrow scroll-left" (click)="scrollLanguages('left')" [disabled]="!canScrollLeft()" aria-label="Scroll languages left">
                <mat-icon>chevron_left</mat-icon>
            </button>
            <div class="lang-buttons" #langButtonsContainer>
                <button class="lang-btn" *ngFor="let langCode of languageCodes()"
                    [class.active]="langCode === currLangCode()"
                    [class.has-errors]="languageValidationStates()[langCode] && !languageValidationStates()[langCode].isValid && languageValidationStates()[langCode].hasContent"
                    [class.valid]="languageValidationStates()[langCode]?.isValid"
                    [class.empty]="!languageValidationStates()[langCode]?.hasContent"
                    [class.lang-queued]="langCode !== 'en' && langUiStates()[langCode] === 'queued'"
                    [class.lang-translating]="langCode !== 'en' && langUiStates()[langCode] === 'translating'"
                    [class.lang-stale]="langCode !== 'en' && langUiStates()[langCode] === 'stale'"
                    [class.lang-failed]="langCode !== 'en' && langUiStates()[langCode] === 'failed'"
                    [matTooltip]="langCode === 'en' ? (languageValidationStates()[langCode]?.hasContent ? getValidationTooltip(langCode) : 'No content') : getLangTooltip(langCode)"
                    (click)="switchLanguage(langCode)">
                    <span class="lang-code">{{ langCode.toUpperCase() }}</span>

                    <!-- Translation status icon for non-EN langs (overrides validation icon) -->
                    @if (langCode !== 'en') {
                      @switch (langUiStates()[langCode]) {
                        @case ('queued') {
                          <mat-icon class="validation-icon lang-status-queued">schedule</mat-icon>
                        }
                        @case ('translating') {
                          <mat-spinner diameter="14" class="lang-spinner"></mat-spinner>
                        }
                        @case ('stale') {
                          <mat-icon class="validation-icon lang-status-stale">change_circle</mat-icon>
                        }
                        @case ('failed') {
                          <mat-icon class="validation-icon lang-status-failed">error_outline</mat-icon>
                        }
                        @default {
                          <!-- done-fresh or empty: fall back to existing validation icons -->
                          @if (languageValidationStates()[langCode]) {
                            @if (languageValidationStates()[langCode].isValid) {
                              <mat-icon class="validation-icon valid">check_circle</mat-icon>
                            } @else if (languageValidationStates()[langCode].hasContent) {
                              <mat-icon class="validation-icon error">error</mat-icon>
                            } @else {
                              <mat-icon class="validation-icon empty">radio_button_unchecked</mat-icon>
                            }
                          }
                        }
                      }
                      <!-- Per-lang retry icon visible only on stale/failed -->
                      @if (canRetryLang(langCode)) {
                        <span class="lang-retry"
                              role="button"
                              [matTooltip]="'Retry ' + langCode.toUpperCase()"
                              (click)="$event.stopPropagation(); retryLang(langCode)">
                          <mat-icon>refresh</mat-icon>
                        </span>
                      }
                    } @else {
                      <!-- EN: keep existing validation icons -->
                      @if (languageValidationStates()[langCode]) {
                        @if (languageValidationStates()[langCode].isValid) {
                          <mat-icon class="validation-icon valid">check_circle</mat-icon>
                        } @else if (languageValidationStates()[langCode].hasContent) {
                          <mat-icon class="validation-icon error">error</mat-icon>
                        } @else {
                          <mat-icon class="validation-icon empty">radio_button_unchecked</mat-icon>
                        }
                      }
                    }
                </button>
            </div>
            <button class="scroll-arrow scroll-right" (click)="scrollLanguages('right')" [disabled]="!canScrollRight()" aria-label="Scroll languages right">
                <mat-icon>chevron_right</mat-icon>
            </button>
        </div>
        <div class="translate-action">
            <button class="btn-translate" (click)="translateTemplate()"
                [disabled]="!canTranslate() || translateInProgress || (langsNeedingWork().length === 0 && langsInFlight().length === 0)"
                [matTooltip]="!canTranslate() ? 'Fix English content errors before translating' : (langsNeedingWork().length === 0 && langsInFlight().length === 0 ? 'All translations are up to date' : 'Translate languages that are empty, stale, or failed')">
                <ng-container *ngIf="translateInProgress || langsInFlight().length > 0">
                    <mat-spinner diameter="18" class="translate-spinner"></mat-spinner>
                    <span>{{ translateButtonLabel() }}</span>
                </ng-container>
                <ng-container *ngIf="!translateInProgress && langsInFlight().length === 0">
                    <mat-icon>translate</mat-icon>
                    <span>{{ translateButtonLabel() }}</span>
                </ng-container>
            </button>
            <span class="last-translated" *ngIf="lastTranslatedRelative()">{{ lastTranslatedRelative() }}</span>
        </div>
    </div>
</div>

<!-- Fullscreen Preview Overlay Template (rendered via CDK Overlay) -->
<ng-template #fullscreenTemplate>
    <div class="fullscreen-preview-overlay" [class.dark-mode]="previewDarkMode()">
        <div class="fullscreen-header">
            <div class="header-left">
                <mat-icon>visibility</mat-icon>
                <span>Preview - {{ displaySubject() || 'No Subject' }}</span>
            </div>
            <div class="header-controls">
                <!-- Dimension Controls -->
                <div class="dimension-controls">
                    <div class="dimension-input">
                        <label>W:</label>
                        <input type="number" [value]="previewCustomWidth()"
                            (change)="updatePreviewDimensions(+$any($event.target).value, previewCustomHeight())"
                            min="320" max="1920" step="10">
                    </div>
                    <span class="dimension-separator">×</span>
                    <div class="dimension-input">
                        <label>H:</label>
                        <input type="number" [value]="previewCustomHeight()"
                            (change)="updatePreviewDimensions(previewCustomWidth(), +$any($event.target).value)"
                            min="400" max="1080" step="10">
                    </div>
                </div>

                <!-- Preset Sizes -->
                <div class="preset-sizes">
                    <button class="preset-btn" (click)="updatePreviewDimensions(375, 667)" matTooltip="iPhone SE">
                        <mat-icon>smartphone</mat-icon>
                        <span>SE</span>
                    </button>
                    <button class="preset-btn" (click)="updatePreviewDimensions(390, 844)" matTooltip="iPhone 14">
                        <mat-icon>smartphone</mat-icon>
                        <span>14</span>
                    </button>
                    <button class="preset-btn" (click)="updatePreviewDimensions(768, 1024)" matTooltip="iPad">
                        <mat-icon>tablet</mat-icon>
                        <span>iPad</span>
                    </button>
                    <button class="preset-btn" (click)="updatePreviewDimensions(1280, 720)" matTooltip="Desktop HD">
                        <mat-icon>desktop_windows</mat-icon>
                        <span>HD</span>
                    </button>
                    <button class="preset-btn" (click)="updatePreviewDimensions(1920, 1080)" matTooltip="Full HD">
                        <mat-icon>desktop_windows</mat-icon>
                        <span>FHD</span>
                    </button>
                </div>

                <!-- Variable Display Toggle -->
                @if (emailForm.value.templateVariables?.length > 0) {
                <button class="control-btn" [class.active]="showVariableValues()" (click)="toggleVariableDisplay()"
                    [matTooltip]="showVariableValues() ? 'Show Placeholders' : 'Show Variable Values'">
                    <mat-icon>{{ showVariableValues() ? 'code_off' : 'data_object' }}</mat-icon>
                </button>
                }

                <!-- Dark Mode Toggle -->
                <button class="control-btn" [class.active]="previewDarkMode()" (click)="togglePreviewDarkMode()"
                    [matTooltip]="previewDarkMode() ? 'Light Mode' : 'Dark Mode'">
                    <mat-icon>{{ previewDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
                </button>

                <!-- Test Email Button -->
                <button class="control-btn" (click)="openTestEmailDialog()" [disabled]="!canSendTestEmail()"
                    [matTooltip]="canSendTestEmail() ? 'Send test email' : testEmailBlockReason()">
                    <mat-icon>send</mat-icon>
                </button>

                <!-- Close Button -->
                <button class="close-btn" (click)="closeFullscreenPreview()" matTooltip="Close Fullscreen">
                    <mat-icon>close</mat-icon>
                </button>
            </div>
        </div>

        <div class="fullscreen-body">
            <div class="preview-frame-wrapper" [style.width.px]="previewCustomWidth()"
                [style.height.px]="previewCustomHeight()">
                <div class="device-frame">
                    <div class="email-client-ui">
                        <div class="email-meta-header">
                            <div class="subject-line">{{ displaySubject() || 'No Subject' }}</div>
                            <div class="sender-info">
                                <div class="avatar"><span>M</span></div>
                                <div class="meta-details">
                                    <div class="from-line">
                                        <span class="name">Mevolve</span>
                                        <span class="email">&lt;info&#64;mevolve.app&gt;</span>
                                    </div>
                                    <div class="to-line">to me</div>
                                </div>
                                <div class="date-info">{{ 0 | date:'shortTime' }}</div>
                            </div>
                        </div>
                        <div class="email-content-wrapper">
                            <iframe class="email-iframe" [srcdoc]="safePreviewHtml()" title="Email Preview"
                                sandbox="allow-same-origin">
                            </iframe>
                        </div>
                    </div>
                </div>
            </div>
            <div class="dimension-indicator">{{ previewCustomWidth() }} × {{ previewCustomHeight() }}px</div>
        </div>
    </div>
</ng-template>
}
